/**
 * Banner that asks the user to enable push notifications.
 *
 * Visibility rules (best practices):
 *  - Hide if push not supported (older Safari, in-app browsers, etc.)
 *  - Hide if user already granted OR denied (don't nag again)
 *  - Hide if dismissed in the last 14 days
 *  - Wait until the page is loaded as a STANDALONE PWA on iOS
 *    (iOS Safari blocks notifications outside installed PWAs)
 *  - Show after 8 seconds to avoid double-prompt with the install banner
 */
import React, { useEffect, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  isPushSupported,
  getNotificationPermission,
  getCurrentSubscription,
  subscribePush,
} from '../lib/pushSubscription';
import { toast } from 'sonner';

const DISMISS_KEY = 'allmx_push_optin_dismissed_v1';
const DISMISS_DAYS = 14;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const PushOptInPrompt = () => {
  const { language } = useLanguage();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const tr = {
    it: {
      title: 'Resta aggiornato',
      desc: 'Ricevi notifiche su offerte, nuovi prodotti e stato del tuo ordine.',
      enable: 'Attiva notifiche',
      later: 'Più tardi',
      ok: 'Notifiche attivate! Riceverai aggiornamenti in tempo reale.',
      err: 'Impossibile attivare le notifiche',
    },
    en: {
      title: 'Stay updated',
      desc: 'Get notified about deals, new products and your order status.',
      enable: 'Enable notifications',
      later: 'Later',
      ok: 'Notifications enabled! You\'ll receive real-time updates.',
      err: 'Could not enable notifications',
    },
    es: {
      title: 'Mantente actualizado',
      desc: 'Recibe notificaciones sobre ofertas, productos nuevos y el estado de tu pedido.',
      enable: 'Activar notificaciones',
      later: 'Más tarde',
      ok: '¡Notificaciones activadas! Recibirás actualizaciones en tiempo real.',
      err: 'No se pudieron activar las notificaciones',
    },
  };
  const t = tr[language] || tr.it;

  useEffect(() => {
    let cancelled = false;

    const evaluate = async () => {
      if (!isPushSupported()) return;
      if (isIOS() && !isStandalone()) return; // iOS requires PWA-installed
      const perm = getNotificationPermission();
      if (perm === 'granted' || perm === 'denied') return;

      // Honour previous dismissals
      const last = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (last && Date.now() - last < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;

      // Don't show if already subscribed (extra safety)
      const existing = await getCurrentSubscription();
      if (existing) return;

      if (!cancelled) {
        setTimeout(() => !cancelled && setShow(true), 8000);
      }
    };
    evaluate();

    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success(t.ok);
      setShow(false);
    } catch (e) {
      toast.error(`${t.err}: ${e.message || ''}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[65] bg-black border border-white/15 rounded-xl shadow-2xl p-4 animate-fade-in"
      data-testid="push-optin-banner"
    >
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Chiudi"
        className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-white/60 hover:text-white"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-500/15 flex items-center justify-center">
          <Bell size={22} className="text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm">{t.title}</h3>
          <p className="text-white/60 text-xs mt-1">{t.desc}</p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={handleEnable}
              disabled={busy}
              className="px-4 py-1.5 rounded text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
              data-testid="push-optin-enable"
            >
              {busy ? '...' : t.enable}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="px-4 py-1.5 rounded text-xs text-white/70 hover:text-white hover:bg-white/10"
            >
              {t.later}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushOptInPrompt;
